/**
 * Toast — Notificações elegantes no lugar de alert()
 * Uso: toast.success('Mensagem') | toast.error('Erro') | toast.info('Info')
 */
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { CheckCircle2, XCircle, Info, AlertTriangle, X } from 'lucide-react';

type ToastType = 'success' | 'error' | 'info' | 'warning';

interface ToastItem {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
}

interface ToastContextValue {
  success: (title: string, message?: string) => void;
  error: (title: string, message?: string) => void;
  info: (title: string, message?: string) => void;
  warning: (title: string, message?: string) => void;
}

const ToastContext = createContext<ToastContextValue>({
  success: () => {},
  error: () => {},
  info: () => {},
  warning: () => {},
});

export const useToast = () => useContext(ToastContext);

const ICONS: Record<ToastType, React.ReactNode> = {
  success: <CheckCircle2 size={18} />,
  error:   <XCircle size={18} />,
  info:    <Info size={18} />,
  warning: <AlertTriangle size={18} />,
};

const STYLES: Record<ToastType, { bg: string; icon: string; bar: string }> = {
  success: { bg: 'bg-white border-l-4 border-emerald-500', icon: 'text-emerald-500', bar: 'bg-emerald-500' },
  error:   { bg: 'bg-white border-l-4 border-red-500',     icon: 'text-red-500',     bar: 'bg-red-500' },
  info:    { bg: 'bg-white border-l-4 border-blue-500',    icon: 'text-blue-500',    bar: 'bg-blue-500' },
  warning: { bg: 'bg-white border-l-4 border-amber-500',   icon: 'text-amber-500',   bar: 'bg-amber-500' },
};

function ToastCard({ item, onClose }: { item: ToastItem; onClose: () => void }) {
  const s = STYLES[item.type];
  useEffect(() => {
    const t = setTimeout(onClose, 4500);
    return () => clearTimeout(t);
  }, [onClose]);

  return (
    <div className={`${s.bg} rounded-xl shadow-modal overflow-hidden animate-in slide-in-from-right-4 fade-in duration-300 max-w-xs w-full`}>
      <div className="flex items-start gap-3 p-3.5">
        <span className={`${s.icon} mt-0.5 flex-shrink-0`}>{ICONS[item.type]}</span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-800 leading-tight">{item.title}</p>
          {item.message && <p className="text-xs text-slate-500 mt-0.5 leading-snug">{item.message}</p>}
        </div>
        <button onClick={onClose} className="text-slate-300 hover:text-slate-500 transition-colors flex-shrink-0 -mt-0.5">
          <X size={14} />
        </button>
      </div>
      {/* Barra de progresso */}
      <div className={`h-0.5 ${s.bar} animate-[shrink_4.5s_linear_forwards]`} />
    </div>
  );
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const add = useCallback((type: ToastType, title: string, message?: string) => {
    const id = Date.now().toString() + Math.random().toString(36).slice(2);
    setToasts(prev => [...prev.slice(-3), { id, type, title, message }]);
  }, []);

  const remove = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const ctx: ToastContextValue = {
    success: (t, m) => add('success', t, m),
    error:   (t, m) => add('error', t, m),
    info:    (t, m) => add('info', t, m),
    warning: (t, m) => add('warning', t, m),
  };

  return (
    <ToastContext.Provider value={ctx}>
      {children}
      {/* Container dos toasts — canto superior direito */}
      <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none">
        {toasts.map(item => (
          <div key={item.id} className="pointer-events-auto">
            <ToastCard item={item} onClose={() => remove(item.id)} />
          </div>
        ))}
      </div>
      <style>{`
        @keyframes shrink {
          from { width: 100%; }
          to   { width: 0%; }
        }
      `}</style>
    </ToastContext.Provider>
  );
}

// ── API global (para usar fora de componentes React) ──────────────────────
type ToastFn = (title: string, message?: string) => void;
interface GlobalToast { success: ToastFn; error: ToastFn; info: ToastFn; warning: ToastFn }

let _toast: GlobalToast = {
  success: (t) => { console.log('[Toast]', t); },
  error:   (t) => { console.error('[Toast]', t); },
  info:    (t) => { console.info('[Toast]', t); },
  warning: (t) => { console.warn('[Toast]', t); },
};

export function ToastBridge() {
  const ctx = useToast();
  useEffect(() => { _toast = ctx; }, [ctx]);
  return null;
}

export const toast = {
  success: (t: string, m?: string) => _toast.success(t, m),
  error:   (t: string, m?: string) => _toast.error(t, m),
  info:    (t: string, m?: string) => _toast.info(t, m),
  warning: (t: string, m?: string) => _toast.warning(t, m),
};
